import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../../config';
import Swal from 'sweetalert2';
import Select from 'react-select';
import loadingGif from '../../assets/loading.gif';
import { 
  Box, 
  Card, 
  CardContent, 
  CircularProgress,
  Grid,
  CardHeader,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  ButtonGroup,
  Switch,
  Typography
} from '@mui/material';
import './ProductManagement.css';
import { ExpandLess, ExpandMore } from '@mui/icons-material';

const ProductManagement = () => {
  const [orgName, setOrgName] = useState('N/A');
  const [department, setDepartment] = useState([]);
  const [categories, setCategories] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [editedDepartmentName, setEditedDepartmentName] = useState({});
  const [units, setUnits] = useState([]);
  const [items, setItems] = useState([]);
  const [createEnabled, setCreateEnabled] = useState(false);
  const [updateEnabled, setUpdateEnabled] = useState(false);
  const [deleteEnabled, setDeleteEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [expandedDepartment, setExpandedDepartment] = useState(null);
  const [expandedCategory, setExpandedCategory] = useState(null);

  const [editedCategoryName, setEditedCategoryName] = useState({});
  const [editedItems, setEditedItems] = useState({});
  const [showUpdateButton, setShowUpdateButton] = useState({});
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [itemsForCategory, setItemsForCategory] = useState([]);
  const [showUpdateDepartmentButton, setShowUpdateDepartmentButton] = useState({});
  
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProductDepartments = async () => {
      const token = localStorage.getItem('token');
      const orgId = localStorage.getItem('selectedOrg');

      if (!token || !orgId) {
        navigate('/unauthorized');
        return;
      }

      setLoading(true);

      try {
        const orgEnabledResponse = await axios.get(`${API_BASE_URL}/org/org-enabled`, {
          headers: {
            Authorization: `Bearer ${token}`,
            SelectedOrg: orgId,
          },
        });

        const { isEnabled } = orgEnabledResponse.data;

        if (!isEnabled) {
          Swal.fire({
            title: 'Organization Disabled',
            text: 'This organization has been disabled by the admin. Please contact support for further assistance.',
            icon: 'error',
            confirmButtonText: 'OK',
          }).then(() => {
            navigate('/unauthorized');
          });

          return;
        }

        const permissions = await axios.get(`${API_BASE_URL}/product/permissions`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { orgId },
        });

        if (permissions.status === 403) {
          navigate('/unauthorized');
          return;
        }

        if (permissions.data) {
          if (!permissions.data.permissions.readPermission) {
            navigate('/unauthorized');
            return;
          }
          setCreateEnabled(permissions.data.permissions.createPermission || false);
          setUpdateEnabled(permissions.data.permissions.updatePermission || false);
          setDeleteEnabled(permissions.data.permissions.deletePermission || false);
        }

        const orgResponse = await axios.get(`${API_BASE_URL}/org/organization-settings/organization-name`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { orgId },
        });

        if (orgResponse.status === 403) {
          navigate('/unauthorized');
          return;
        }
        setOrgName(orgResponse.data.orgName || 'N/A');


        const productUnits = await axios.get(`${API_BASE_URL}/product/units`, {
          headers: {
            Authorization: `Bearer ${token}`,
            selectedOrg: orgId,
          },
        });

        setUnits(productUnits.data.unitsMap);

        const productCategories = await axios.get(`${API_BASE_URL}/product/departments`, {
          headers: {
            Authorization: `Bearer ${token}`,
            selectedOrg: orgId,
          },
        });

        setDepartment(productCategories.data || []);

      } catch (error) {
        console.log('Failed to fetch Org Members:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProductDepartments();

  }, [navigate]);
  
  

  const handleCreateProductCategory = async () => {
    const token = localStorage.getItem('token');
    const orgId = localStorage.getItem('selectedOrg');

    Swal.fire({
      title: 'Creating...',
      text: 'Please wait while we create the product category.',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    }); 

    try {
      const response = await fetch(`${API_BASE_URL}/product/create-category`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          SelectedOrg: orgId,
        },
      });

      Swal.close();

      if (response.status === 201) {
        Swal.fire({
          title: 'Success!',
          text: 'Product category created successfully.',
          icon: 'success',
          confirmButtonText: 'OK',
        }).then(() => {
          window.location.reload();
        });
      } else if (response.status === 422) {
        Swal.fire({
          title: 'Limit Reached!',
          text: 'Maximum product categories reached for the current plan. Please upgrade your plan or delete a category before creating a new one.',
          icon: 'warning',
          confirmButtonText: 'OK',
        });
      }
    } catch (error) {
      console.error('Failed to create product category:', error);
      Swal.close();
      Swal.fire({
        title: 'Error!',
        text: 'Failed to create product category. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
      }).then(() => {
        window.location.reload();
      });
    }
  };

  const fetchProductDepartmentsNoLoading = async () => {
      const token = localStorage.getItem('token');
      const orgId = localStorage.getItem('selectedOrg');

      if (!token || !orgId) {
        navigate('/unauthorized');
        return;
      }

      try {
        const orgEnabledResponse = await axios.get(`${API_BASE_URL}/org/org-enabled`, {
          headers: {
            Authorization: `Bearer ${token}`,
            SelectedOrg: orgId,
          },
        });

        const { isEnabled } = orgEnabledResponse.data;

        if (!isEnabled) {
          Swal.fire({
            title: 'Organization Disabled',
            text: 'This organization has been disabled by the admin. Please contact support for further assistance.',
            icon: 'error',
            confirmButtonText: 'OK',
          }).then(() => {
            navigate('/unauthorized');
          });

          return;
        }

        const permissions = await axios.get(`${API_BASE_URL}/product/permissions`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { orgId },
        });

        if (permissions.status === 403) {
          navigate('/unauthorized');
          return;
        }

        if (permissions.data) {
          if (!permissions.data.permissions.readPermission) {
            navigate('/unauthorized');
            return;
          }
          setCreateEnabled(permissions.data.permissions.createPermission || false);
          setUpdateEnabled(permissions.data.permissions.updatePermission || false);
          setDeleteEnabled(permissions.data.permissions.deletePermission || false);
        }

        const orgResponse = await axios.get(`${API_BASE_URL}/org/organization-settings/organization-name`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { orgId },
        });

        if (orgResponse.status === 403) {
          navigate('/unauthorized');
          return;
        }
        setOrgName(orgResponse.data.orgName || 'N/A');


        const productUnits = await axios.get(`${API_BASE_URL}/product/units`, {
          headers: {
            Authorization: `Bearer ${token}`,
            selectedOrg: orgId,
          },
        });

        setUnits(productUnits.data.unitsMap);

        const productCategories = await axios.get(`${API_BASE_URL}/product/departments`, {
          headers: {
            Authorization: `Bearer ${token}`,
            selectedOrg: orgId,
          },
        });

        setDepartment(productCategories.data || []);

      } catch (error) {
        console.log('Failed to fetch Org Members:', error);
      }
    };

  const handleCreateProductDepartment = async () => {
    const token = localStorage.getItem('token');
    const orgId = localStorage.getItem('selectedOrg');

    Swal.fire({
      title: 'Creating...',
      text: 'Please wait while we create the product category.',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    }); 

    try {
      const response = await fetch(`${API_BASE_URL}/product/create-department`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          SelectedOrg: orgId,
        },
      });

      Swal.close();

      if (response.status === 201) {
        Swal.fire({
          title: 'Success!',
          text: 'Product department created successfully.',
          icon: 'success',
          confirmButtonText: 'OK',
        }).then(() => {
          fetchProductDepartmentsNoLoading();
        });
      } else if (response.status === 422) {
        Swal.fire({
          title: 'Limit Reached!',
          text: 'Maximum product departments reached for the current plan. Please upgrade your plan or delete a department before creating a new one.',
          icon: 'warning',
          confirmButtonText: 'OK',
        });
      }
    } catch (error) {
      console.error('Failed to create product department:', error);
      Swal.close();
      Swal.fire({
        title: 'Error!',
        text: 'Failed to create product department. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
      }).then(() => {
        window.location.reload();
      });
    }
  };

  const handleUpdateCategory = async (departmentId, categoryId, categoryName, active) => {
    const token = localStorage.getItem('token');
    const orgId = localStorage.getItem('selectedOrg');

    try {
      const response = await axios.post(`${API_BASE_URL}/product/update-category`, {
        categoryId: categoryId,
        categoryName: categoryName,
        active: active
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
          SelectedOrg: orgId,
        },
      });

      Swal.close();
      
      if (response.status === 201) {
        Swal.fire({
          title: 'Success!',
          text: 'Product category updated successfully.',
          icon: 'success',
          confirmButtonText: 'OK',
        }).then(() => {
          handleExpandDepartmentNoLoading(departmentId);
        });
      }
    } catch (error) {
      console.error('Failed to update product category:', error);
      Swal.close();
      Swal.fire({
        title: 'Error!',
        text: 'Failed to update product category. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
      });
    }
  };

  const handleUpdateDepartment = async (categoryId, categoryName, active) => {
    const token = localStorage.getItem('token');
    const orgId = localStorage.getItem('selectedOrg');

    try {
      const response = await axios.post(`${API_BASE_URL}/product/update-department`, {
        departmentId: categoryId,
        departmentName: categoryName,
        active: active
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
          SelectedOrg: orgId,
        },
      });

      Swal.close();
      
      if (response.status === 201) {
        Swal.fire({
          title: 'Success!',
          text: 'Product department updated successfully.',
          icon: 'success',
          confirmButtonText: 'OK',
        }).then(() => {
          fetchProductDepartmentsNoLoading(categoryId);
        });
      }
    } catch (error) {
      console.error('Failed to update product department:', error);
      Swal.close();
      Swal.fire({
        title: 'Error!',
        text: 'Failed to update product department. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
      });
    }
  };

  const handleExpandCategoryNoLoading = async (categoryId) => {
    const token = localStorage.getItem('token');
    const orgId = localStorage.getItem('selectedOrg');
  
    setExpandedCategory(categoryId);
  
    try {
      const productItems = await axios.get(`${API_BASE_URL}/product/items`, {
        headers: {
          Authorization: `Bearer ${token}`,
          SelectedOrg: orgId,
        },
        params: { categoryId: categoryId },
      });

      const itemsMap = productItems.data.itemsMap;

      // Set items for the current category
      setItems((prevItems) => ({
        ...prevItems,
        [categoryId]: itemsMap,
      }));

      // Initialize editedItems for the current category
      setEditedItems((prevEditedItems) => {
        const newEditedItems = { ...prevEditedItems };
        itemsMap.forEach(item => {
          newEditedItems[item.itemId] = {
            itemUnit: item.itemUnit,
            itemName: item.itemName,
            itemPrice: item.itemPrice,
            itemStock: item.itemStock,
          };
        });
        return newEditedItems;
      });
    } catch (error) {
      if (error.response && error.response.status === 404) {
        
        // Set empty items array for the category to show "No items found"
        setItems((prevItems) => ({
          ...prevItems,
          [categoryId]: [],
        }));
      } else {
        console.error('Failed to fetch product items:', error);
      }
    }
  };


  const handleCreateItem = async (categoryId) => { 
    const token = localStorage.getItem('token');
    const orgId = localStorage.getItem('selectedOrg');

    try {
      const response = await axios.post(`${API_BASE_URL}/product/create-item`, {
        categoryId: categoryId
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
          SelectedOrg: orgId,
        },
      });

      Swal.close();

      if (response.status === 201) {
        Swal.fire({
          title: 'Success!',
          text: 'Product item created successfully.',
          icon: 'success',
          confirmButtonText: 'OK',
        }).then(() => {
          handleExpandCategoryNoLoading(categoryId);
        });
      } 
    } catch (error) {
      Swal.close();
      
      // Check if the error response is a 422
      if (error.response && error.response.status === 422) {
        Swal.fire({
          title: 'Limit Reached!',
          text: 'Maximum product item reached for the category. Please upgrade your plan or delete a category before creating a new one.',
          icon: 'warning',
          confirmButtonText: 'OK',
        });
      } else {
        console.error('Failed to create product item:', error);
        Swal.fire({
          title: 'Error!',
          text: 'Failed to create product item. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
      }
    }
  };

  const handleCreateCategory = async (departmentId) => { 
    const token = localStorage.getItem('token');
    const orgId = localStorage.getItem('selectedOrg');

    try {
      const response = await axios.post(`${API_BASE_URL}/product/create-category`, {
        departmentId: departmentId
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
          SelectedOrg: orgId,
        },
      });

      Swal.close();

      if (response.status === 201) {
        Swal.fire({
          title: 'Success!',
          text: 'Product category created successfully.',
          icon: 'success',
          confirmButtonText: 'OK',
        }).then(() => {
          handleExpandDepartmentNoLoading(departmentId);
        });
      } 
    } catch (error) {
      Swal.close();
      
      // Check if the error response is a 422
      if (error.response && error.response.status === 422) {
        Swal.fire({
          title: 'Limit Reached!',
          text: 'Maximum product category reached for the department. Please upgrade your plan or delete a category before creating a new one.',
          icon: 'warning',
          confirmButtonText: 'OK',
        });
      } else {
        console.error('Failed to create product category:', error);
        Swal.fire({
          title: 'Error!',
          text: 'Failed to create product category. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
      }
    }
  };


  const handleDeleteProductCategory = async (departmentId, categoryName, categoryId) => {
    const token = localStorage.getItem('token');
    const orgId = localStorage.getItem('selectedOrg');

    const result = await Swal.fire({
      title: `Are you sure you want to delete "${categoryName}"?`,
      text: "This action cannot be undone.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    });

    if (result.isConfirmed) {

      try {
        const response = await axios.post(`${API_BASE_URL}/product/delete-category`, {
          categoryId: categoryId
        }, {
          headers: {
            Authorization: `Bearer ${token}`,
            SelectedOrg: orgId,
          },
        });

        Swal.close();
        
        if (response.status === 201) {
          Swal.fire({
            title: 'Success!',
            text: 'Product item deleted successfully.',
            icon: 'success',
            confirmButtonText: 'OK',
          }).then(() => {
            handleExpandDepartmentNoLoading(departmentId);
          });
        }
      } catch (error) {
        console.error('Failed to delete item category:', error);
        Swal.close();
        Swal.fire({
          title: 'Error!',
          text: 'Failed to delete item category. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
      }
    }
  };

  const handleDeleteProductDepartment = async (departmentName, departmentId) => {
    const token = localStorage.getItem('token');
    const orgId = localStorage.getItem('selectedOrg');

    const result = await Swal.fire({
      title: `Are you sure you want to delete "${departmentName}"?`,
      text: "All items in this department will be deleted. This action cannot be undone.",

      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    });

    if (result.isConfirmed) {

      try {
        const response = await axios.post(`${API_BASE_URL}/product/delete-department`, {
          departmentId: departmentId
        }, {
          headers: {
            Authorization: `Bearer ${token}`,
            SelectedOrg: orgId,
          },
        });

        Swal.close();
        
        if (response.status === 201) {
          Swal.fire({
            title: 'Success!',
            text: 'Product department deleted successfully.',
            icon: 'success',
            confirmButtonText: 'OK',
          }).then(() => {
            fetchProductDepartmentsNoLoading();
          });
        }
      } catch (error) {
        console.error('Failed to delete item category:', error);
        Swal.close();
        Swal.fire({
          title: 'Error!',
          text: 'Failed to delete product department. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
      }
    }
  };
  
  
  const handleExpandDepartment = async (categoryId) => {
    const token = localStorage.getItem('token');
    const orgId = localStorage.getItem('selectedOrg');

    setExpandedDepartment(expandedDepartment === categoryId ? null : categoryId);
    
    if (expandedDepartment !== categoryId) {
      try {
        const productItems = await axios.get(`${API_BASE_URL}/product/categories`, {
          headers: {
            Authorization: `Bearer ${token}`,
            SelectedOrg: orgId,
          },
          params: { departmentId: categoryId },
        });
  
        const categoriesMap = productItems.data.categoriesMap;

        setCategories(categoriesMap);
  
        // Initialize editedItems for the current category
        setEditedDepartmentName((prevEditedItems) => {
        const newEditedDepartment = { ...prevEditedItems };
        if (Array.isArray(categoriesMap)) {
          categoriesMap.forEach(category => {

            newEditedDepartment[category.categoryId] = {
              categoryName: category.categoryName,
              isEnabled: category.isEnabled
            };
          });
        }
        return newEditedDepartment;
      });
      } catch (error) {
        if (error.response && error.response.status === 404) {
          
          // Set empty items array for the category to show "No items found"
          setItems((prevItems) => ({
            ...prevItems,
            [categoryId]: [],
          }));
        } else {
          console.error('Failed to fetch product items:', error);
        }
      }
    }
  }; 

  const handleExpandDepartmentNoLoading = async (categoryId) => {
    const token = localStorage.getItem('token');
    const orgId = localStorage.getItem('selectedOrg');

    setExpandedDepartment(categoryId);
  
    try {
      const productItems = await axios.get(`${API_BASE_URL}/product/categories`, {
        headers: {
          Authorization: `Bearer ${token}`,
          SelectedOrg: orgId,
        },
        params: { departmentId: categoryId },
      });

      const categoriesMap = productItems.data.categoriesMap;

      setCategories(categoriesMap);

      // Initialize editedItems for the current category
      setEditedDepartmentName((prevEditedItems) => {
      const newEditedDepartment = { ...prevEditedItems };
      if (Array.isArray(categoriesMap)) {
        categoriesMap.forEach(category => {

          newEditedDepartment[category.categoryId] = {
            categoryName: category.categoryName,
            isEnabled: category.isEnabled
          };
        });
      }
      return newEditedDepartment;
    });
    } catch (error) {
      if (error.response && error.response.status === 404) {
        
        // Set empty items array for the category to show "No items found"
        setItems((prevItems) => ({
          ...prevItems,
          [categoryId]: [],
        }));
      } else {
        console.error('Failed to fetch product items:', error);
      }
    }
    
  }; 

  const handleExpandCategory = async (category) => {
    const token = localStorage.getItem('token');
    const orgId = localStorage.getItem('selectedOrg');
  
    setExpandedCategory(expandedCategory === category.categoryId ? null : category.categoryId);
  
    if (expandedCategory !== category.categoryId) {

      try {
        const productItems = await axios.get(`${API_BASE_URL}/product/items`, {
          headers: {
            Authorization: `Bearer ${token}`,
            SelectedOrg: orgId,
          },
          params: { categoryId: category.categoryId },
        });
  
        const itemsMap = productItems.data.itemsMap;

        // Set items for the current category
        setItems((prevItems) => ({
          ...prevItems,
          [category.categoryId]: itemsMap,
        }));
  
        // Initialize editedItems for the current category
        setEditedItems((prevEditedItems) => {
          const newEditedItems = { ...prevEditedItems };
          itemsMap.forEach(item => {
            newEditedItems[item.itemId] = {
              itemUnit: item.itemUnit,
              itemName: item.itemName,
              itemPrice: item.itemPrice,
              itemStock: item.itemStock,
            };
          });
          return newEditedItems;
        });
      } catch (error) {
        if (error.response && error.response.status === 404) {
          
          // Set empty items array for the category to show "No items found"
          setItems((prevItems) => ({
            ...prevItems,
            [category.categoryId]: [],
          }));
        } else {
          console.error('Failed to fetch product items:', error);
        }
      }
    }
  };

  const handleItemStatusChange = async (categoryId, itemId, newStatus) => {
    const token = localStorage.getItem('token');
    const orgId = localStorage.getItem('selectedOrg');

    try {
      const response = await axios.post(`${API_BASE_URL}/product/update-item-status`, {
        itemId: itemId,
        active: newStatus
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
          SelectedOrg: orgId,
        },
      });

      Swal.close();
      
      if (response.status === 201) {
        Swal.fire({
          title: 'Success!',
          text: 'Product item updated successfully.',
          icon: 'success',
          confirmButtonText: 'OK',
        }).then(() => {
          handleExpandCategoryNoLoading(categoryId);
        });
      }
    } catch (error) {
      console.error('Failed to update product item:', error);
      Swal.close();
      Swal.fire({
        title: 'Error!',
        text: 'Failed to update product item. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
      });
    }
  }

  const handleUpdateItem = async (categoryId, itemId) => {
    const token = localStorage.getItem('token');
    const orgId = localStorage.getItem('selectedOrg');
    
    try {
      const response = await axios.post(`${API_BASE_URL}/product/update-item`, {
        itemId: itemId,
        itemName: editedItems[itemId].itemName,
        itemPrice: parseFloat(editedItems[itemId].itemPrice).toFixed(2),
        itemStock: Math.max(editedItems[itemId].itemStock, -1),
        itemUnit: editedItems[itemId].itemUnit
      }, { 
        headers: {
          Authorization: `Bearer ${token}`,
          SelectedOrg: orgId,
        },
      });

      Swal.close();
      
      if (response.status === 201) {
        Swal.fire({
          title: 'Success!',
          text: 'Product item updated successfully.',
          icon: 'success',
          confirmButtonText: 'OK',
        }).then(() => {
          handleExpandCategoryNoLoading(categoryId);
        });
      }
    } catch (error) {
      console.error('Failed to update product item:', error);
      Swal.close();
      Swal.fire({
        title: 'Error!',
        text: 'Failed to update product item. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
      });
    }
  };
  
  const hasItemChanged = (itemId, item) => {
    const editedItem = editedItems[itemId] || {};
    return (
      editedItem.itemName !== item.itemName ||
      parseFloat(editedItem.itemPrice).toFixed(2) !== parseFloat(item.itemPrice).toFixed(2) ||
      Math.max(editedItem.itemStock, -1) !== item.itemStock ||
      editedItem.itemUnit !== item.itemUnit
    );
  };
  

  const handleDeleteItem = async (categoryId, itemId, itemName) => {
    const token = localStorage.getItem('token');
    const orgId = localStorage.getItem('selectedOrg');

    const result = await Swal.fire({
      title: `Are you sure you want to delete "${itemName}"?`,
      text: "This action cannot be undone.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    });

    if (result.isConfirmed) {
    
      try {
        const response = await axios.post(`${API_BASE_URL}/product/delete-item`, {
          itemId: itemId,
        }, { 
          headers: {
            Authorization: `Bearer ${token}`,
            SelectedOrg: orgId,
          },
        });

        Swal.close();
        
        if (response.status === 201) {
          Swal.fire({
            title: 'Success!',
            text: 'Product item updated successfully.',
            icon: 'success',
            confirmButtonText: 'OK',
          }).then(() => {
            handleExpandCategoryNoLoading(categoryId);
          });
        }
      } catch (error) {
        console.error('Failed to update product item:', error);
        Swal.close();
        Swal.fire({
          title: 'Error!',
          text: 'Failed to update product item. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
      }
    }
  }

  if (loading) {
    return (
      <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100vh - 64px)',
          backgroundColor: '#fff',
      }}>
          <img src={loadingGif} alt="Loading..." style={{ width: '30%', height: '30%', objectFit: 'contain' }} />
      </div>
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: '#f5f5f5', width: '100%' }}>
      <Card sx={{ width: '100%', p: 4, boxShadow: 3 }}>
        <div className="organizations-header">
          <h1>Product Management: {orgName}</h1>
          {createEnabled && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleCreateProductDepartment}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Create Product Department
              </Button>
            </div>
          )}
          
        </div>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Department</TableCell>
                <TableCell>Enabled</TableCell>
                <TableCell>Options</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {department
              .sort((a, b) => a.product_department_name.localeCompare(b.product_department_name))
              .map((dept) => (
                <React.Fragment key={dept.product_department_id}>
                  <TableRow>
                     <TableCell>
                      <Box display="flex" alignItems="center">
                        {expandedDepartment === dept.product_department_id ? (
                          <ExpandLess onClick={() => setExpandedDepartment(null)} />
                        ) : (
                          <ExpandMore onClick={() => handleExpandDepartment(dept.product_department_id)} />
                        )}
                        <TextField
                          value={editedDepartmentName[dept.product_department_id] ?? dept.product_department_name}
                          onChange={(e) => {
                            const newName = e.target.value;
                            setEditedDepartmentName((prev) => ({ ...prev, [dept.product_department_id]: newName }));
                            setShowUpdateDepartmentButton((prev) => ({
                              ...prev,
                              [dept.product_department_id]: newName !== dept.product_department_name,
                            }));
                          }}
                          variant="outlined"
                          size="small"
                          multiline
                          rows={1}
                        />
                        {showUpdateDepartmentButton[dept.product_department_id] && (
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() =>
                              handleUpdateDepartment(
                                dept.product_department_id,
                                editedDepartmentName[dept.product_department_id],
                                dept.is_enabled
                              )
                            }
                          >
                            Update
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={dept.is_enabled}
                        onChange={() =>
                          handleUpdateDepartment(
                                dept.product_department_id,
                                editedDepartmentName[dept.product_department_id] || dept.product_department_name,
                                !dept.is_enabled
                              )
                            }
                          />
                        </TableCell>
                        <TableCell>
                      <ButtonGroup>
                        {createEnabled && (
                          <Button
                            variant="contained"
                            size="small"product_department_id
                            onClick={() => handleCreateCategory(dept.product_department_id)}
                          >
                            Create Category
                          </Button>
                        )}
                        {deleteEnabled && (
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={() =>
                              handleDeleteProductDepartment(
                                dept.product_department_name,
                                dept.product_department_id
                              )
                            }
                          >
                            Delete
                          </Button>
                        )}
                      </ButtonGroup>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={3}>
                      <Collapse in={expandedDepartment === dept.product_department_id} timeout="auto" unmountOnExit>
                        <Box margin={1}>
                          <Typography variant="h6" gutterBottom component="div">
                            Categories
                          </Typography>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Category</TableCell>
                                <TableCell>Enabled</TableCell>
                                <TableCell>Options</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {categories
                              ?.sort((a, b) => a.categoryName.localeCompare(b.categoryName))
                              .map((category) => (
                                <React.Fragment key={category}>
                                  <TableRow>
                                    <TableCell>
                                      <Box display="flex" alignItems="center">
                                        {expandedCategory === category.categoryId ? (
                                          <ExpandLess onClick={() => setExpandedCategory(null)} />
                                        ) : (
                                          <ExpandMore onClick={() => handleExpandCategory(category)} />
                                        )}
                                        <TextField
                                          value={editedCategoryName[category.categoryId] ?? category.categoryName}
                                          onChange={(e) => {
                                            const newName = e.target.value;
                                            setEditedCategoryName((prev) => ({ ...prev, [category.categoryId]: newName }));
                                            setShowUpdateButton((prev) => ({
                                              ...prev,
                                              [category.categoryId]: newName !== category.categoryName,
                                            }));
                                          }}
                                          variant="outlined"
                                          size="small"
                                          multiline
                                          rows={1}
                                        />
                                        {showUpdateButton[category.categoryId] && (
                                          <Button
                                            variant="contained"
                                            size="small"
                                            onClick={() =>
                                              handleUpdateCategory(
                                                dept.product_department_id,
                                                category.categoryId,
                                                editedCategoryName[category.categoryId],
                                                category.isEnabled
                                              )
                                            }
                                          >
                                            Update
                                          </Button>
                                        )}
                                      </Box>
                                    </TableCell>
                                    <TableCell>
                                      <Switch
                                        checked={category.isEnabled}
                                        onChange={() =>
                                          handleUpdateCategory(
                                            dept.product_department_id,
                                            category.categoryId,
                                            editedCategoryName[category.categoryId] || category.categoryName,
                                            !category.isEnabled
                                          )
                                        }
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <ButtonGroup>
                                        {createEnabled && (
                                          <Button
                                            variant="contained"
                                            size="small"
                                            onClick={() => handleCreateItem(category.categoryId)}
                                          >
                                            Create Item
                                          </Button>
                                        )}
                                        {deleteEnabled && (
                                          <Button
                                            variant="outlined"
                                            color="error"
                                            size="small"
                                            onClick={() =>
                                              handleDeleteProductCategory(
                                                dept.product_department_id,
                                                category.categoryName,
                                                category.categoryId
                                              )
                                            }
                                          >
                                            Delete
                                          </Button>
                                        )}
                                      </ButtonGroup>
                                    </TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={3}>
                                      <Collapse in={expandedCategory === category.categoryId} timeout="auto" unmountOnExit>
                                        <Box margin={1}>
                                          <Typography variant="h6" gutterBottom component="div">
                                            Items
                                          </Typography>
                                          <Table size="small">
                                            <TableHead>
                                              <TableRow>
                                                <TableCell>Item Name</TableCell>
                                                <TableCell>Price</TableCell>
                                                <TableCell>Stock</TableCell>
                                                <TableCell>Unit</TableCell>
                                                <TableCell>Actions</TableCell>
                                                <TableCell>Enabled</TableCell>
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {items[category.categoryId]
                                              ?.sort((a, b) => a.itemName.localeCompare(b.itemName))
                                              .map((item) => (
                                                <TableRow key={item.itemId}>
                                                  <TableCell>
                                                    <TextField
                                                      fullWidth
                                                      variant="outlined"
                                                      size="small"
                                                      value={editedItems[item.itemId]?.itemName ?? item.itemName}
                                                      onChange={(e) =>
                                                        setEditedItems((prev) => ({
                                                          ...prev,
                                                          [item.itemId]: { ...prev[item.itemId], itemName: e.target.value },
                                                        }))
                                                      }
                                                    />
                                                  </TableCell>
                                                  <TableCell>
                                                    <TextField
                                                      type="number"
                                                      variant="outlined"
                                                      size="small"
                                                      value={editedItems[item.itemId]?.itemPrice ?? item.itemPrice}
                                                      onChange={(e) =>
                                                        setEditedItems((prev) => ({
                                                          ...prev,
                                                          [item.itemId]: { ...prev[item.itemId], itemPrice: e.target.value },
                                                        }))
                                                      }
                                                    />
                                                  </TableCell>
                                                  <TableCell>
                                                    <TextField
                                                      type="number"
                                                      variant="outlined"
                                                      size="small"
                                                      value={editedItems[item.itemId]?.itemStock ?? item.itemStock}
                                                      onChange={(e) =>
                                                        setEditedItems((prev) => ({
                                                          ...prev,
                                                          [item.itemId]: { ...prev[item.itemId], itemStock: e.target.value },
                                                        }))
                                                      }
                                                    />
                                                  </TableCell>
                                                  <TableCell>
                                                    <Select
                                                      options={units.map((unit) => ({
                                                        value: unit.unitId,
                                                        label: `${unit.unitName} (${unit.unitSymbol})`,
                                                      }))}
                                                      value={
                                                        editedItems[item.itemId]?.itemUnit
                                                          ? {
                                                              value: editedItems[item.itemId].itemUnit,
                                                              label: `${units.find((unit) => unit.unitId === editedItems[item.itemId].itemUnit).unitName} (
                                                                ${units.find((unit) => unit.unitId === editedItems[item.itemId].itemUnit).unitSymbol}
                                                              )`,
                                                            }
                                                          : null
                                                      }
                                                      onChange={(selectedOption) => {
                                                        if (selectedOption) {
                                                          setEditedItems((prev) => ({
                                                            ...prev,
                                                            [item.itemId]: {
                                                              ...prev[item.itemId],
                                                              itemUnit: selectedOption.value,
                                                            },
                                                          }));
                                                        }
                                                      }}
                                                    />
                                                  </TableCell>
                                                  <TableCell>
                                                    <ButtonGroup>
                                                      {updateEnabled && (
                                                        <Button
                                                          variant="contained"
                                                          color="primary"
                                                          size="small"
                                                          onClick={() => handleUpdateItem(category.categoryId, item.itemId)}
                                                          disabled={!hasItemChanged(item.itemId, item)}
                                                        >
                                                          Update
                                                        </Button>
                                                      )}
                                                      {deleteEnabled && (
                                                        <Button
                                                          variant="outlined"
                                                          color="error"
                                                          size="small"
                                                          onClick={() => handleDeleteItem(category.categoryId, item.itemId, item.itemName)}
                                                        >
                                                          Delete
                                                        </Button>
                                                      )}
                                                    </ButtonGroup>
                                                  </TableCell>
                                                  <TableCell>
                                                    <Switch
                                                      checked={item.isEnabled}
                                                      onChange={() => handleItemStatusChange(category.categoryId, item.itemId, !item.isEnabled)}
                                                      disabled={!updateEnabled}
                                                    />
                                                  </TableCell>
                                                </TableRow>
                                              ))}
                                            </TableBody>
                                          </Table>
                                        </Box>
                                      </Collapse>
                                    </TableCell>
                                  </TableRow>
                                </React.Fragment>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
    </>
  );
};

export default ProductManagement;